import CmsBanner from "../../components/Banner/CmsBanner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyAchievementsAPI, setDefaultAchievementAPI } from "../../api/game";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader/Loader";
import useToast from "../../hooks/useToast";
import GoldShild from "../../assets/images/myachievement/Glod.png"
import SilverShild from "../../assets/images/myachievement/Sliver.png"
import BronzeShild from "../../assets/images/myachievement/Bronze.png"
import SilverReb from "../../assets/images/myachievement/Silver-Reb.png"
import BronzeReb from "../../assets/images/myachievement/Bronze-Reb.png"
import GoldReb from "../../assets/images/myachievement/Gold-Reb.png"
import AchevImg from "../../assets/images/myachievement/Achie-img.png"
import AchevReb from "../../assets/images/myachievement/Achev-Reb.png"
import AchievementShild from "../../assets/images/myachievement/Achie-Shild.png"
import DefaultBg from "../../assets/images/myachievement/default.png"
import { useEffect, useMemo, useState } from "react";
import { IoInformation } from "react-icons/io5";
import { clearNewAchievementEarnedAPI, getCategoryBadgeListAPI, getGameCategoryListAPI } from "../../api/user";
import useAppSelector from "../../hooks/useAppSelector";
import { useDispatch } from "react-redux";
import { updateUser } from "../../redux/slices/userSlice";
import { resolveAssetUrl } from "../../utils/mediaType";

type CategoryBadgeRow = {
  id?: string;
  name?: { en?: string; ar?: string } | string;
  image?: string;
};

type TierKey = "Gold" | "Silver" | "Bronze";

function getLocalizedField(value: unknown, lang: string): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const obj = value as { en?: string; ar?: string };
    return obj[lang as "en" | "ar"] || obj.en || obj.ar || "";
  }
  return "";
}

function normalizeTierKey(name: string): TierKey | null {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === "gold" || normalized.includes("gold") || normalized === "ذهب" || normalized === "ذهبي") return "Gold";
  if (normalized === "silver" || normalized.includes("silver") || normalized === "فضة" || normalized === "فضي") return "Silver";
  if (normalized === "bronze" || normalized.includes("bronze") || normalized === "برونز" || normalized === "برونزي") return "Bronze";
  return null;
}

function tierShieldUrlByEnglishName(categoryBadgeList: { data?: CategoryBadgeRow[] } | undefined) {
  const list = categoryBadgeList?.data;
  if (!Array.isArray(list)) return {} as Record<string, string>;
  return list.reduce<Record<string, string>>((acc, item) => {
    const image = resolveAssetUrl(item.image);
    if (!image) return acc;

    const en = typeof item.name === "object" && item.name !== null ? item.name.en : typeof item.name === "string" ? item.name : "";
    const ar = typeof item.name === "object" && item.name !== null ? item.name.ar : "";
    const enKey = String(en || "").trim().toLowerCase();
    const arKey = String(ar || "").trim().toLowerCase();

    if (enKey) acc[enKey] = image;
    if (arKey) acc[arKey] = image;

    const tierKey = normalizeTierKey(en) || normalizeTierKey(ar);
    if (tierKey) acc[tierKey.toLowerCase()] = image;

    return acc;
  }, {});
}

function resolveTierShieldUrl(map: Record<string, string>, badgeName: string) {
  if (!badgeName) return undefined;
  const key = badgeName.trim().toLowerCase();
  if (map[key]) return map[key];

  const tierKey = normalizeTierKey(badgeName);
  if (tierKey && map[tierKey.toLowerCase()]) return map[tierKey.toLowerCase()];

  return undefined;
}

function buildOptionalPreviewUrls(badgeTier: any, tierShieldUrl?: string): string[] {
  const fromArray = Array.isArray(badgeTier?.images) ? badgeTier.images.filter((u: unknown) => typeof u === "string" && u) : [];
  const singles = [badgeTier?.category_image, badgeTier?.categoryImage, badgeTier?.image].filter(
    (u): u is string => typeof u === "string" && Boolean(u)
  );
  const candidates = [...fromArray, ...singles];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of candidates) {
    const resolved = resolveAssetUrl(u);
    if (!resolved || resolved === tierShieldUrl) continue;
    if (seen.has(resolved)) continue;
    seen.add(resolved);
    out.push(resolved);
    if (out.length >= 4) break;
  }
  return out;
}

// import YellowStar from "../../assets/images/myachievement/yellow-star.png"

const Badge = ({
  categoryImage,
  title,
  secondtitle,
  description = "",
  isUnlocked = false,
  isDefault = false,
  badgeTierId = "",
  disableClick = false,
  categoryName = "",
  tierShieldUrl = "",
  previewImageUrls = [] as string[],
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: setDefaultAchievement, isPending: isSettingDefault } = useMutation({
    mutationFn: setDefaultAchievementAPI,
    onSuccess: (data) => {
      useToast(data?.message || "Default achievement set successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["my-achievements"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: any) => {
      useToast(error.message || "Failed to set default achievement", "error");
    },
  });

  const handleSetDefaultAchievement = (badgeTierId: string) => {
    if (isSettingDefault) return;
    if (isDefault) return;
    setDefaultAchievement({ badge_tier_id: badgeTierId });
  };

  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const badgeTier = {
    Gold: {
      image: GoldShild,
      reb: GoldReb,
    },
    Silver: {
      image: SilverShild,
      reb: SilverReb,
    },
    Bronze: {
      image: BronzeShild,
      reb: BronzeReb,
    },
  };

  const tierKey = normalizeTierKey(secondtitle);
  const resolvedTierUrl = tierShieldUrl ? resolveAssetUrl(tierShieldUrl) : "";
  const shieldDisplaySrc = resolvedTierUrl || (tierKey ? badgeTier[tierKey].image : undefined);
  const rebDisplaySrc = tierKey ? badgeTier[tierKey].reb : undefined;

  return (
    <div className={`flex flex-col items-center 2xl:w-[calc(25%-1.25vw)] lg:w-[calc(25%-24px)] sm:w-[calc(33.33%-18px)] xs:w-[calc(33.33%-11px)] w-[calc(50%-8px)]${!isUnlocked ? "opacity-50 grayscale" : ""}`}>
      <div className="flex flex-col items-center w-full gap-2">
        <div className="flex flex-wrap justify-center gap-2 items-start w-full">
          <button
            type="button"
            disabled={!isUnlocked || disableClick}
            onClick={() => {
              if (isUnlocked) handleSetDefaultAchievement(badgeTierId);
            }}
            className={`relative shrink-0 w-[100px] h-[100px] overflow-hidden flex items-center justify-center ${isUnlocked ? "cursor-pointer hover:opacity-95" : "cursor-default"}`}
            aria-label={t("myAchievements.badges") || "Category badge"}
          >
            {shieldDisplaySrc ? (
              <img src={shieldDisplaySrc} alt="" className="w-full h-full object-contain" />
            ) : null}
          </button>
          {previewImageUrls.map((url) => (
            <button
              key={url}
              type="button"
              onClick={() => {
                if (isUnlocked) handleSetDefaultAchievement(badgeTierId);
              }}
              className="relative shrink-0 w-[100px] h-[100px] rounded-lg  overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-95"
              aria-label="Preview image"
            >
              <img src={url} alt="" className="w-full h-full object-contain" />
            </button>
          ))}
        </div>
        <div className="relative flex justify-center items-center w-[min(100%,200px)] max-w-full h-10 mx-auto">
          {rebDisplaySrc ? <img src={rebDisplaySrc} alt="" className="w-full h-full object-contain" /> : null}
          <p
            className={`absolute inset-0 flex justify-center items-center text-center text-sm/tight md:text-base/tight font-bold px-1 ${!isUnlocked ? "text-gray-500" : "text-black-300"}`}
          >
            {categoryName}
          </p>
        </div>
      </div>
      {/* Default label */}
      {isDefault && (
        <div className="mt-2 2xl:mt-[0.5vw] flex justify-center items-center w-full">
          <div
            className="relative bg-center bg-no-repeat bg-contain 2xl:w-[8vw] xl:w-[140px] lg:w-[120px] md:w-[100px] w-[80px] 2xl:h-[2.5vw] xl:h-[45px] lg:h-[40px] md:h-[35px] h-[30px] flex items-center justify-center"
            style={{ backgroundImage: `url(${DefaultBg})` }}
          >
            <p className="2xl:text-[1.25vw] xl:text-xl md:text-lg text-base font-bold text-black-300 text-center whitespace-nowrap">
              {t("myAchievements.default") || "Default"}
            </p>
          </div>
        </div>
      )}

      {imagePreviewUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-60 p-4"
          onClick={() => setImagePreviewUrl(null)}
          role="presentation"
        >
          <div
            className="relative max-w-[min(90vw,720px)] max-h-[min(85dvh,720px)] p-2 bg-white-500 rounded-xl border-[1.5px] border-black-300 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setImagePreviewUrl(null)}
              className="absolute -top-2 -end-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-yellow border-[1.5px] border-black-300 shadow-[0px_2px_0px_#110706] hover:scale-105 transition-transform"
              aria-label="Close"
            >
              <span className="text-black-300 font-bold leading-none text-lg">×</span>
            </button>
            <img
              src={imagePreviewUrl}
              alt=""
              className="max-w-full max-h-[min(80dvh,680px)] w-auto h-auto object-contain mx-auto block rounded-md"
            />
          </div>
        </div>
      )}
    </div>

  );
};


const Achievement = ({ title, description = '', isUnlocked = false, isDefault = false, badgeTierId = '', onInfoClick, type, image }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: setDefaultAchievement, isPending: isSettingDefault } = useMutation({
    mutationFn: setDefaultAchievementAPI,
    onSuccess: (data) => {
      useToast(data?.message || "Default achievement set successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["my-achievements"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: any) => {
      useToast(error.message || "Failed to set default achievement", "error");
    },
  });

  const handleSetDefaultAchievement = (badgeTierId: string, isUnlocked: boolean) => {
    if (!isUnlocked) return;

    if (type === 'streak') {
      // useToast(t("myAchievements.streakAchievementCannotBeSetAsDefault"), "info");
      return;
    } else {
      if (isSettingDefault) return;
      if (isDefault) return;
      setDefaultAchievement({ achievement_id: badgeTierId });
    }
  };
  const achievementTier = {
    image: resolveAssetUrl(image) || AchevImg,
    reb: AchevReb,
    shild: AchievementShild,
  };

  return (
    <div className={`flex flex-col items-center 2xl:w-[calc(25%-1.25vw)] lg:w-[calc(25%-24px)] sm:w-[calc(33.33%-18px)] xs:w-[calc(33.33%-11px)] w-[calc(50%-8px)]${!isUnlocked ? 'opacity-50 grayscale' : ''}`}>
      <div onClick={() => handleSetDefaultAchievement(badgeTierId, isUnlocked)} className={`flex flex-col items-center 2xl:h-[15.417vw] lg:h-[296px] md:h-60 h-[200px] w-full `}>
        <div className="bg-center bg-no-repeat 2xl:bg-size-[10.521vw_15.417vw] xl:bg-size-[202px_296px] lg:bg-size-[180px_280px] md:bg-size-[150px_230px] bg-size-[110px_180px] relative w-full h-full"
        // style={{ backgroundImage: `url(${achievementTier.shild})` }}
        >
          <div className="w-full 2xl:h-[11.979vw] xl:h-[230px] lg:h-[210px] md:h-40 h-[130px] mx-auto relative 2xl:top-[2.396vw] xl:top-11.5 lg:top-13 md:top-11  top-10"><img src={achievementTier.image} alt="category-img" className="w-full h-full object-contain" /></div>
          {/* <div className="absolute 2xl:bottom-[1.25vw] lg:bottom-6 md:bottom-7 bottom-7 start-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2 flex justify-center items-center lg:w-full md:w-[200px] w-[140px] 2xl:h-[3.698vw] xl:h-[60px] md:h-[50px] h-10">
            <img src={achievementTier.reb} alt="start" className="w-full h-full" />
            <p className={`absolute inset-0 2xl:mt-[0.417vw] mt-2 flex justify-center items-center text-center 2xl:text-[1.458vw]/tight xl:text-xl/tight md:text-lg/tight  lg:text-lg/tight text-sm/tight font-bold ${isUnlocked ? 'text-black-300' : 'text-gray-500'}`}>
              {title}
            </p>
          </div> */}
          {/* Info icon at top right */}
          {description && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onInfoClick?.({ title, description });
              }}
              className="absolute top-5 end-7 md:end-5 md:top-7 xl:end-1 xl:top-5 w-4 h-4 md:w-6 md:h-6 flex items-center justify-center bg-primary-yellow border-[1.5px] border-black-300 rounded-full shadow-[0px_2px_0px_#110706] transition-all z-10 hover:scale-102"
              aria-label="Achievement information"
            >
              <IoInformation className="text-black-300 h-full w-full" />
            </button>
          )}
        </div>
      </div>
      {/* Default label */}
      {isDefault && (
        // mt-2 2xl:mt-[0.5vw]
        <div className="2xl:-mt-[3vw] -mt-12 flex justify-center items-center w-full">
          <div
            className="relative bg-center bg-no-repeat bg-contain 2xl:w-[8vw] xl:w-[140px] lg:w-[120px] md:w-[100px] w-[80px] 2xl:h-[2.5vw] xl:h-[45px] lg:h-[40px] md:h-[35px] h-[30px] flex items-center justify-center"
            style={{ backgroundImage: `url(${DefaultBg})` }}
          >
            <p className="2xl:text-[1.25vw] xl:text-xl md:text-lg text-base font-bold text-black-300 text-center whitespace-nowrap">
              {t("myAchievements.default") || "Default"}
            </p>
          </div>
        </div>
      )}
    </div>

  );
};

export default function MyAchievements() {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  const dispatch = useDispatch();
  const user = useAppSelector((state: any) => state.user.user);
  const [achievementInfoPopup, setAchievementInfoPopup] = useState<{ title: string; description: string } | null>(null);
  const [hasHandledNewAchievementFlag, setHasHandledNewAchievementFlag] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: clearNewAchievementEarned } = useMutation({
    mutationFn: clearNewAchievementEarnedAPI,
    onSuccess: () => {
      dispatch(updateUser({ new_achievement_earned: false }));
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: any) => {
      useToast(error.message || t("achievementNotification.markReadFailed"), "error");
    },
  });

  useEffect(() => {
    if (!user?.new_achievement_earned) {
      setHasHandledNewAchievementFlag(false);
    }
  }, [user?.new_achievement_earned]);

  useEffect(() => {
    if (!user?.new_achievement_earned || hasHandledNewAchievementFlag) return;
    setHasHandledNewAchievementFlag(true);
    clearNewAchievementEarned();
  }, [user?.new_achievement_earned, hasHandledNewAchievementFlag, clearNewAchievementEarned]);

  // Fetch achievements from API
  const { data: achievementsData, isLoading, error } = useQuery({
    queryKey: ["my-achievements", currentLanguage],
    queryFn: getMyAchievementsAPI,
  });

  const { data: categoryList } = useQuery({
    queryKey: ["categoryList"],
    queryFn: () => getGameCategoryListAPI({ page: 1 }),
  });

  const { data: categoryBadgeList } = useQuery({
    queryKey: ["category-badge-list"],
    queryFn: getCategoryBadgeListAPI,
  });

  const tierShieldMap = useMemo(() => tierShieldUrlByEnglishName(categoryBadgeList), [categoryBadgeList]);
  // Map API data to badges format
  const badgeTiers = achievementsData?.data?.badges || [];
  const mappedBadgeTiers = badgeTiers?.map((badgeTier: any) => ({
    ...badgeTier,
    title: getLocalizedField(badgeTier.category_name, currentLanguage) || '',
    categoryImage: resolveAssetUrl(badgeTier.category_image) || '',
    secondtitle: getLocalizedField(badgeTier.badge_name, currentLanguage) || getLocalizedField(badgeTier.badge_name, "en") || '',
    description: badgeTier.description || '',
    is_default: badgeTier.is_default || false,
    image: resolveAssetUrl(badgeTier.image) || '',
    isUnlocked: true,
  }));

  const SampleBadgeTiers = categoryList?.data[0]?.categories?.slice(0, 3).map((category: any, index: number) => ({
    title: getLocalizedField(category.name, currentLanguage) || 'Category Name',
    categoryImage: resolveAssetUrl(category.image) || '',
    categoryName: getLocalizedField(category.name, currentLanguage) || '',
    secondtitle: index % 2 === 0 ? 'Gold' : index % 3 === 0 ? 'Silver' : 'Bronze',
    description: category.description || '',
    is_default: false,
    isUnlocked: false,
    id: category.id,
  }));
  // Map all achievements (streak and played)
  const allStreakAchievements = achievementsData?.data?.allStreakAchievements || [];
  const allPlayedAchievements = achievementsData?.data?.allPlayedAchievements || [];

  // Combine all achievements and mark default
  const allAchievements = [
    ...allStreakAchievements.map((achievement: any) => ({
      id: achievement.id,
      achievement_id: achievement.id,
      title: achievement.name,
      description: achievement.description,
      isUnlocked: achievement.is_achieved,
      type: 'streak',
      image: resolveAssetUrl(achievement.image) || '',
      isDefault: achievement.is_default,
    })),
    ...allPlayedAchievements.map((achievement: any) => ({
      id: achievement.id,
      achievement_id: achievement.id,
      title: achievement.name,
      description: achievement.description,
      isUnlocked: achievement.is_achieved,
      type: 'played',
      image: resolveAssetUrl(achievement.image) || '',
      isDefault: achievement.is_default,
    })),
  ].sort((a: any, b: any) => b.isUnlocked - a.isUnlocked);

  return (
    <>
      <div className="wrapper bg-primary-ivory">
        <CmsBanner
          title={t("myAchievements.banner.title")}
          description={t("myAchievements.banner.description")}
          classNameShape="hidden"
        />
        {/* Unlocked Achievements Section */}
        <div className="w-full 2xl:max-w-[63vw] max-w-280 mx-auto 2xl:py-[3.5vw] lg:py-15 py-10 relative z-20 flex flex-col items-center justify-between">

          <h2 className="xl:text-[2.5vw]/tight md:text-4xl/tight text-3xl/tight text-primary-rose font-bold text-center 2xl:mb-[1vw] mb-3">
            {t("myAchievements.badges")}
          </h2>
          <div className="flex flex-wrap justify-center w-full 2xl:gap-[1.667vw] xl:gap-8 md:gap-4 gap-2">

            {isLoading ? (
              <div className="flex justify-center items-center h-full"><Loader /></div>
            ) : error ? (
              <div>{t("myAchievements.error")}</div>
            ) : mappedBadgeTiers.length > 0 ? (
              mappedBadgeTiers.map((badgeTier, index) => {
                const badgeNameForTier =
                  getLocalizedField(badgeTier.badge_name, "en") || badgeTier.secondtitle || getLocalizedField(badgeTier.badge_name, currentLanguage) || "";
                const tierUrl = resolveTierShieldUrl(tierShieldMap, badgeNameForTier);
                const previewUrls = buildOptionalPreviewUrls(badgeTier, tierUrl);
                return (
                  <Badge
                    categoryName={getLocalizedField(badgeTier.category_name, currentLanguage) || badgeTier.title}
                    key={badgeTier.id || index}
                    categoryImage={badgeTier.categoryImage}
                    title={badgeTier.title}
                    secondtitle={badgeTier.secondtitle}
                    description={badgeTier.description}
                    isUnlocked={badgeTier.isUnlocked}
                    isDefault={badgeTier.is_default}
                    badgeTierId={badgeTier.id}
                    tierShieldUrl={tierUrl || ""}
                    previewImageUrls={previewUrls}
                  />
                );
              })
            ) : (
              SampleBadgeTiers?.map((badgeTier, index) => {
                const tierUrl = resolveTierShieldUrl(tierShieldMap, badgeTier.secondtitle || "");
                const previewUrls = buildOptionalPreviewUrls(badgeTier, tierUrl);
                return (
                  <Badge
                    categoryName={badgeTier.categoryName}
                    key={badgeTier.id || index}
                    categoryImage={badgeTier.categoryImage}
                    title={badgeTier.title}
                    secondtitle={badgeTier.secondtitle}
                    description={badgeTier.description}
                    isUnlocked={badgeTier.isUnlocked}
                    isDefault={badgeTier.is_default}
                    badgeTierId={badgeTier.id}
                    tierShieldUrl={tierUrl || ""}
                    previewImageUrls={previewUrls}
                  />
                );
              })
            )}
          </div>

        </div>

        {/* More Achievements Section */}
        <div className="w-full 2xl:max-w-[63vw] max-w-280 mx-auto 2xl:py-[3.5vw] lg:py-15 py-10 2xl:px-[0.833vw] xl:px-10 px-4 relative z-20 flex flex-col items-center justify-between">
          <h2 className="xl:text-[2.5vw]/tight md:text-4xl/tight text-3xl/tight text-primary-rose font-bold text-center 2xl:mb-[1vw] mb-3">
            {t("myAchievements.achievements")}
          </h2>
          <div className="flex flex-wrap justify-center w-full 2xl:gap-[1.667vw] xl:gap-8 md:gap-4 gap-2">
            {allAchievements?.length > 0 ? (
              allAchievements.map((achievement, index) => (
                <Achievement
                  image={achievement.image}
                  badgeTierId={achievement.achievement_id}
                  key={achievement.id}
                  title={achievement.title}
                  description={achievement.description}
                  isUnlocked={achievement.isUnlocked}
                  isDefault={achievement.isDefault}
                  type={achievement.type}
                  onInfoClick={setAchievementInfoPopup}
                />
              ))
            ) : (
              <div className="font-bold lg:text-xl/tight text-lg/tight 2xl:text-[1.25vw]/[1.4vw] text-center w-full">{t("myAchievements.noAchievementsToDisplay")}</div>
            )}
          </div>
        </div>



        {/* Achievement Info Popup */}
        {achievementInfoPopup && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50"
            onClick={() => setAchievementInfoPopup(null)}
          >
            <div
              dir={currentLanguage === "ar" ? "rtl" : "ltr"}
              className="modal bg-white-500 lg:rounded-[1.5vw] rounded-3xl 2xl:p-[2vw] md:p-8 p-6 2xl:max-w-[35vw] md:max-w-[600px] max-w-[90%] w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex 2xl:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] mb-3">
                <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange">
                  {achievementInfoPopup.title}
                </h1>
                {/* Close Button */}
                <button
                  onClick={() => setAchievementInfoPopup(null)}
                  className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none"
                >
                  <svg
                    className="w-full h-full"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                      fill="#333938"
                    />
                  </svg>
                </button>
              </div>

              {/* Divider */}
              <div className="text-center lg:mb-[1.4vw] md:mb-6 mb-4">
                <hr className="border-0 2xl:h-[0.053vw] h-px w-full 2xl:mb-[1vw] md:mb-4 mb-3 2xl:mt-[0.5vw] mt-2 [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
              </div>

              {/* Content */}
              <div>
                <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold break-words">
                  {achievementInfoPopup.description}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}